import { orderBy } from 'lodash';
import moment from 'moment/moment';

import {
  GET_LEADS, SET_LEADS, GET_LEAD, SET_LEAD,
  SET_LEAD_VIEW, SET_LEAD_DOCUMENTS, SET_LEAD_ACTIVITY,
  CLEAR_LEAD, GET_LEAD_NOTES, SET_LEAD_NOTES,
} from './actionTypes';

import firebase, { collections, storageRefs } from '../firebase';

import { updateUser } from './usersActions';
import { updateSurgeon } from './surgeonsActions';

import { fromMomentToTimestampDay, nowTimestampUTC } from '../utils/date';
import { getFileWithParameters, post } from '../utils/api';
import { canAccessSurgeon, hasAllSurgeonsAccess } from '../utils/users';

import { leadStatuses, LOST_LEADS_PIPELINE_ID, ACTIVE_SURGEONS_PIPELINE_ID } from '../constants/sales';
import { roleNames, territoryRoles } from '../constants/userRoles';
import urls from '../constants/urls';

const { SALES_REP, EXTERNAL_SALES_REP, SURGEON, PRACTICE_MANAGER } = roleNames;

export const setLeads = (leads) => ({ type: SET_LEADS, leads });

export const clearLead = () => ({ type: CLEAR_LEAD });

export const getLeads = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const currentUser = state.user.currentUser;
  const hospitals = state.hospitals?.list || [];
  const surgeons = state.surgeons?.list || [];
  const path = collections.LEADS_COLLECTION(tenantId);

  dispatch({ type: GET_LEADS });

  const snapshot = await firebase.db.collection(path).get();
  let leads = snapshot.docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : null,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
      date: data.date ? data.date.toDate() : null,
      pipelineUpdatedAt: data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : null,
      stageUpdatedAt: data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : null,
      lostAt: data.lostAt ? data.lostAt.toDate() : null,
      lostDate: data.lostDate ? data.lostDate.toDate() : null,
      wonAt: data.wonAt ? data.wonAt.toDate() : null,
    };
  });

  if ([SALES_REP, EXTERNAL_SALES_REP, SURGEON, PRACTICE_MANAGER].includes(currentUser.role) && !hasAllSurgeonsAccess(currentUser)) {
    // if (!currentUser.assignedSurgeons || !currentUser.assignedSurgeons.length) {
    //   return dispatch(setLeads([]));
    // }

    leads = leads.filter((l) => canAccessSurgeon(currentUser, l.surgeon) || l.createdBy === currentUser.uid || l.owner === currentUser.uid);
  }

  if (territoryRoles?.includes(currentUser.role) && !!currentUser.territoryVisibility) {
    const userHospitals = hospitals?.filter((h) => h.territories?.some((territory) => currentUser?.assignedTerritories?.includes(territory)));

    leads = leads?.filter((l) => {
      const surgeon = surgeons.find((s) => s.id === l.surgeon);
      return surgeon?.hospitals?.some((hospitalId) => userHospitals.find((h) => h.id === hospitalId)) || l.createdBy === currentUser.uid || l.owner === currentUser.uid
    });
  }

  dispatch(setLeads(orderBy(leads, 'title', 'asc')));

  return orderBy(leads, 'title', 'asc');
};

export const getLeadsWithActivity = (pipelineId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const currentUser = state.user.currentUser;
  const hospitals = state.hospitals?.list || [];
  const surgeons = state.surgeons?.list || [];
  const path = collections.LEADS_COLLECTION(tenantId);

  dispatch({ type: GET_LEADS });

  const snapshot = await firebase.db.collection(path).get();
  let leads = snapshot.docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : null,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
      date: data.date ? data.date.toDate() : null,
      pipelineUpdatedAt: data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : null,
      stageUpdatedAt: data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : null,
      lostAt: data.lostAt ? data.lostAt.toDate() : null,
      lostDate: data.lostDate ? data.lostDate.toDate() : null,
      wonAt: data.wonAt ? data.wonAt.toDate() : null,
    };
  });

  if ([SALES_REP, EXTERNAL_SALES_REP, SURGEON, PRACTICE_MANAGER].includes(currentUser.role) && !hasAllSurgeonsAccess(currentUser)) {
    // if (!currentUser.assignedSurgeons || !currentUser.assignedSurgeons.length) {
    //   return dispatch(setLeads([]));
    // }

    leads = leads.filter((l) => canAccessSurgeon(currentUser, l.surgeon) || l.createdBy === currentUser.uid || l.owner === currentUser.uid);
  }

  if (territoryRoles?.includes(currentUser.role) && !!currentUser.territoryVisibility) {
    const userHospitals = hospitals?.filter((h) => h.territories?.some((territory) => currentUser?.assignedTerritories?.includes(territory)));

    leads = leads?.filter((l) => {
      const surgeon = surgeons.find((s) => s.id === l.surgeon);
      return surgeon?.hospitals?.some((hospitalId) => userHospitals.find((h) => h.id === hospitalId)) || l.createdBy === currentUser.uid || l.owner === currentUser.uid
    });
  }

  leads = await Promise.all(
    leads.map(async (lead) => {
      if (lead.pipeline === pipelineId) {
        const activity = await dispatch(getLeadActivity(lead.id));
        return {
          ...lead,
          activity: activity || [],
        }
      }
      return lead;
    })
  );

  dispatch(setLeads(orderBy(leads, 'title', 'asc')));

  return orderBy(leads, 'title', 'asc');
};

export const getSurgeonLeads = (surgeonId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.LEADS_COLLECTION(tenantId);

  const snapshot = await firebase.db.collection(path).where('surgeon', '==', surgeonId).get();
  const leads = snapshot.docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : null,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
      date: data.date ? data.date.toDate() : null,
      pipelineUpdatedAt: data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : null,
      stageUpdatedAt: data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : null,
      lostAt: data.lostAt ? data.lostAt.toDate() : null,
      lostDate: data.lostDate ? data.lostDate.toDate() : null,
      wonAt: data.wonAt ? data.wonAt.toDate() : null,
    };
  });

  return orderBy(leads.filter((l) => !!l.active), 'title', 'asc');
};

export const getStageLeads = (stageId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.LEADS_COLLECTION(tenantId);

  dispatch({ type: GET_LEADS });

  const snapshot = await firebase.db.collection(path).where('stage', '==', stageId).get();
  const leads = snapshot.docs?.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : null,
      updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
      date: data.date ? data.date.toDate() : null,
      pipelineUpdatedAt: data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : null,
      stageUpdatedAt: data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : null,
      lostAt: data.lostAt ? data.lostAt.toDate() : null,
      lostDate: data.lostDate ? data.lostDate.toDate() : null,
      wonAt: data.wonAt ? data.wonAt.toDate() : null,
    };
  });

  return orderBy(leads.filter((l) => !!l.active), 'title', 'asc');
};

export const setLead = (lead) => ({ type: SET_LEAD, lead });

export const getLead = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const path = collections.LEADS_COLLECTION(tenantId);

  dispatch({ type: GET_LEAD });

  const doc = await firebase.db.collection(path).doc(leadId).get();
  const data = doc.data();

  const lead = {
    ...data,
    id: doc.exists ? doc.id : null,
    createdAt: data && data.createdAt ? data.createdAt.toDate() : '',
    updatedAt: data && data.updatedAt ? data.updatedAt.toDate() : '',
    date: data && data.date ? data.date.toDate() : '',
    pipelineUpdatedAt: data && data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : '',
    stageUpdatedAt: data && data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : '',
    lostAt: data.lostAt ? data.lostAt.toDate() : null,
    lostDate: data.lostDate ? data.lostDate.toDate() : null,
    wonAt: data.wonAt ? data.wonAt.toDate() : null,
  };

  dispatch(setLead(lead));

  return lead;
};

export const setLeadView = (view) => ({ type: SET_LEAD_VIEW, view });

export const createLead = (leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const users = state.users?.list || [];
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);

  const doc = {
    ...leadData,
    active: true,
    status: leadStatuses.ACTIVE.value,
    date: leadData.date ? fromMomentToTimestampDay(leadData.date) : null,
    createdBy: userId,
    createdAt: nowTimestampUTC(),
    updatedAt: nowTimestampUTC(),
    pipelineUpdatedAt: nowTimestampUTC(),
    stageUpdatedAt: nowTimestampUTC(),
  }

  if (tenantId) {
    const snap = await firebase.db.collection(path).add(doc);

    const owner = users.find((u) => u.uid === leadData.owner);
    if (owner && owner.role === SALES_REP && !!owner.assignedSurgeons && !canAccessSurgeon(owner, leadData.surgeon)) {
      await dispatch(updateUser({
        ...owner,
        assignedSurgeons: [...owner.assignedSurgeons, leadData.surgeon],
      }, true));
    }

    return {
      id: snap.id,
      ...doc,
    };
  } else {
    throw new Error('Invalid tenant');
  }
};

export const sendNewSurgeonCreatedEmail = (surgeonName) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const createdBy = state.user.currentUser ? `${state.user.currentUser.firstName} ${state.user.currentUser.lastName}` : '';
  const idToken = await firebase.auth.currentUser.getIdToken();

  if (tenantId) {
    await post(urls.leads.sendSurgeonCreatedEmail(tenantId), { surgeonName, createdBy }, idToken);
  } else {
    throw new Error('Invalid tenant');
  }
};

export const updateLead = (leadId, leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const users = state.users?.list || [];
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    const doc = {
      ...leadData,
      date: leadData.date ? fromMomentToTimestampDay(leadData.date) : null,
      updatedAt: nowTimestampUTC(),
      updatedBy: userId
    }
    await firebase.db.collection(path).doc(leadId).set(doc, { merge: true });

    const owner = users.find((u) => u.uid === leadData.owner);
    if (owner && owner.role === SALES_REP && !!owner.assignedSurgeons && !canAccessSurgeon(owner, leadData.surgeon)) {
      await dispatch(updateUser({
        ...owner,
        assignedSurgeons: [...owner.assignedSurgeons, leadData.surgeon],
      }, true));
    }
  } else {
    throw new Error('Invalid tenant');
  }
};

export const createActiveSurgeonLead = (leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);

  const doc = {
    ...leadData,
    active: true,
    status: leadStatuses.WON.value,
    pipeline: ACTIVE_SURGEONS_PIPELINE_ID,
    date: leadData.date ? fromMomentToTimestampDay(leadData.date) : null,
    createdBy: userId,
    updatedBy: userId,
    createdAt: nowTimestampUTC(),
    updatedAt: nowTimestampUTC(),
    pipelineUpdatedAt: nowTimestampUTC(),
    stageUpdatedAt: nowTimestampUTC(),
    wonAt: nowTimestampUTC(),
    wonBy: userId,
  }

  if (tenantId) {
    const snap = await firebase.db.collection(path).add(doc);
    return {
      id: snap.id,
      ...doc,
    };
  } else {
    throw new Error('Invalid tenant');
  }
};

export const makeLeadLost = (leadId, leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    const doc = {
      ...leadData,
      lostDate: leadData.lostDate ? fromMomentToTimestampDay(leadData.lostDate) : null,
      lostAt: nowTimestampUTC(),
      lostBy: userId,
      status: leadStatuses.LOST.value,
      lostNotes: leadData?.lostNotes || '',
      pipeline: LOST_LEADS_PIPELINE_ID,
      stage: leadData?.stage || null,
      owner: leadData.owner || '',
    }

    const stageLeads = await dispatch(getStageLeads(leadData.stage));
    doc.orderNumber = stageLeads.length + 1;

    await firebase.db.collection(path).doc(leadId).set(doc, { merge: true });

    if (leadData.lostNotes) {
      await dispatch(createLeadNote(leadId, { note: leadData.lostNotes }));
    }
  } else {
    throw new Error('Invalid tenant');
  }
};

export const makeLeadWon = (leadId, leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    const doc = {
      ...leadData,
      pipeline: ACTIVE_SURGEONS_PIPELINE_ID,
      wonAt: nowTimestampUTC(),
      wonBy: userId,
      status: leadStatuses.WON.value,
    }
    await firebase.db.collection(path).doc(leadId).set(doc, { merge: true });

    if (leadData.surgeonDetails?.email || leadData.surgeonDetails?.phone) {
      const surgeonDoc = {
        email: leadData.surgeonDetails?.email || '',
        phone: leadData.surgeonDetails?.phone || '',
        speciality: leadData.surgeonDetails?.speciality || '',
      }
      await dispatch(updateSurgeon(leadData.surgeon, surgeonDoc));
    }
  } else {
    throw new Error('Invalid tenant');
  }
};

export const reactivateLead = (leadId, leadData) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    const doc = {
      ...leadData,
      date: leadData.date ? fromMomentToTimestampDay(leadData.date) : null,
      lostAt: leadData.lostAt ? fromMomentToTimestampDay(leadData.lostAt) : null,
      lostDate: null,
      status: leadStatuses.ACTIVE.value,
      updatedAt: nowTimestampUTC(),
      updatedBy: userId,
      reactivatedAt: nowTimestampUTC(),
      reactivatedBy: userId,
    }

    await firebase.db.collection(path).doc(leadId).set(doc, { merge: true });
  } else {
    throw new Error('Invalid tenant');
  }
};

export const deleteLead = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    await firebase.db.collection(path).doc(leadId).delete();
  } else {
    throw new Error('Invalid tenant');
  }
};

export const archiveLead = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEADS_COLLECTION(tenantId);

  if (tenantId) {
    const doc = {
      active: false,
      stage: null,
      orderNumber: null,
    }
    await firebase.db.collection(path).doc(leadId).set(doc, { merge: true });
  } else {
    throw new Error('Invalid tenant');
  }
};

export const bulkUpdateLeads = (leads) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEADS_COLLECTION(tenantId);
  const batch = firebase.db.batch();

  if (tenantId) {
    leads.forEach((lead) => {
      const ref = firebase.db.collection(path).doc(lead.id);
      const doc = {
        ...lead,
        updatedAt: nowTimestampUTC(),
        updatedBy: userId,
      }
      batch.update(ref, doc);
    });

    await batch.commit();
  } else {
    throw new Error('Invalid tenant');
  }
};

// Activity

export const setLeadActivity = (activity) => ({ type: SET_LEAD_ACTIVITY, activity });

export const getLeadActivity = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEAD_ACTIVITY_COLLECTION(tenantId, leadId);

  const snapshot = await firebase.db.collection(path).get();
  const activity = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : '',
      bookingData: data?.bookingData ? {
        ...data?.bookingData,
        date: data.bookingData?.date ? data.bookingData.date?.toDate() : '',
      } : null
    };
  });

  dispatch(setLeadActivity(orderBy(activity, 'createdAt', 'desc')));

  return orderBy(activity, 'createdAt', 'desc');
};

export const createLeadActivity = (leadId, data) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const userId = state.user.currentUser.uid;
  const path = collections.LEAD_ACTIVITY_COLLECTION(tenantId, leadId);

  const doc = {
    category: data?.category || '',
    type: data?.category || '',
    title: data?.title || '',
    payload: data?.payload || '',
    createdAt: nowTimestampUTC(),
    userId,
    arguments: {
      leadId,
    },
    bookingData: {
      date: data?.date ? data.date?.toDate() : null,
      duration: data?.duration || null,
    }
  };

  await firebase.db.collection(path).add(doc);
};

export const updateLeadActivity = (leadId, activityId, data) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEAD_ACTIVITY_COLLECTION(tenantId, leadId);

  const doc = {
    category: data?.category || '',
    type: data?.category || '',
    title: data?.title || '',
    payload: data?.payload || '',
    updatedAt: nowTimestampUTC(),
    bookingData: {
      date: data?.date ? data.date?.toDate() : null,
      duration: data?.duration || null,
    }
  };

  await firebase.db.collection(path).doc(activityId).set(doc, { merge: true });
};

// Documents

export const setLeadDocuments = (documents) => ({ type: SET_LEAD_DOCUMENTS, documents });

export const getLeadDocuments = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEAD_DOCUMENTS_COLLECTION(tenantId, leadId);

  const snapshot = await firebase.db.collection(path).get();
  const documents = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : '',
    };
  });

  return dispatch(setLeadDocuments(documents));
};

export const uploadDocuments = (leadId, files) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const promises = [];
  const userId = state.user.currentUser.uid;

  files.forEach((file) => {
    promises.push(uploadDocument(tenantId, leadId, file, userId));
  });

  await Promise.all(promises);
};

const uploadDocument = async (tenantId, leadId, file, userId) => {
  const storageRef = firebase.storage.ref();
  const storagePath = storageRefs.LEAD_DOCUMENTS_REF(tenantId, leadId);
  const filePath = `${storagePath}/${file.name}`;
  const ref = storageRef.child(filePath);
  const snap = await ref.put(file);
  const downloadUrl = await snap.ref.getDownloadURL();
  const fileObj = {
    active: true,
    fileName: file.name,
    path: filePath,
    downloadUrl,
    createdAt: nowTimestampUTC(),
    userId,
  };
  const path = collections.LEAD_DOCUMENTS_COLLECTION(tenantId, leadId);

  await firebase.db.collection(path).add(fileObj);
};

export const onUploadComplete = (leadId, file) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state?.tenant?.currentTenant?.id;
  const userId = state.user.currentUser.uid;
  const storagePath = storageRefs.LEAD_DOCUMENTS_REF(tenantId, leadId);
  const filePath = `${storagePath}/${file.name}`;
  const fileObj = {
    active: true,
    fileName: file.name,
    path: filePath,
    downloadUrl: file.downloadUrl,
    createdAt: nowTimestampUTC(),
    userId,
  };
  const path = collections.LEAD_DOCUMENTS_COLLECTION(tenantId, leadId);

  await firebase.db.collection(path).add(fileObj);
};

export const deleteDocument = (leadId, fileId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEAD_DOCUMENTS_COLLECTION(tenantId, leadId);

  await firebase.db.collection(path).doc(fileId).delete();
};

// Notes

export const setLeadNotes = (notes) => ({ type: SET_LEAD_NOTES, notes });

export const getLeadNotes = (leadId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const path = collections.LEAD_NOTES_COLLECTION(tenantId, leadId);

  dispatch({ type: GET_LEAD_NOTES });

  const snapshot = await firebase.db.collection(path).get();
  const notes = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id,
      createdAt: data.createdAt ? data.createdAt.toDate() : '',
    };
  });

  return dispatch(setLeadNotes(notes));
};

export const createLeadNote = (leadId, noteData, connectedTenantId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = connectedTenantId || state?.tenant?.currentTenant?.id;
  const currentUser = state.user.currentUser;
  const path = collections.LEAD_NOTES_COLLECTION(tenantId, leadId);

  if (tenantId) {
    await firebase.db.collection(path).add({
      ...noteData,
      active: true,
      createdAt: nowTimestampUTC(),
      authorFullName: `${currentUser.firstName} ${currentUser.lastName}`,
      authorId: currentUser.uid,
      leadId,
    });
  } else {
    throw new Error('Invalid tenant');
  }
};

export const updateLeadNote = (leadId, noteId, noteData, connectedTenantId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = connectedTenantId || state?.tenant?.currentTenant?.id;
  const path = collections.LEAD_NOTES_COLLECTION(tenantId, leadId);

  if (tenantId) {
    await firebase.db.collection(path).doc(noteId).set({
      ...noteData,
      updatedAt: nowTimestampUTC(),
    }, { merge: true });
  } else {
    throw new Error('Invalid tenant');
  }
};

export const deleteLeadNote = (leadId, noteId, connectedTenantId) => async (dispatch, getState) => {
  const state = getState();
  const tenantId = connectedTenantId || state?.tenant?.currentTenant?.id;
  const path = collections.LEAD_NOTES_COLLECTION(tenantId, leadId);

  if (tenantId) {
    await firebase.db.collection(path).doc(noteId).delete();
  } else {
    throw new Error('Invalid tenant');
  }
};

// Export

export const exportLeads = () => async (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const idToken = await firebase.auth.currentUser.getIdToken();

  const today = moment().format('DD-MM-YY');

  try {
    await getFileWithParameters(urls.leads.exportLeads(tenantId), idToken, `sales_export_${today}`, 'xlsx');
  } catch (err) {
    throw new Error(err);
  }
};

// Subscriptions

export const subscribeToLeads = () => (dispatch, getState) => {
  const state = getState();
  const tenantId = state.tenant.currentTenant ? state.tenant.currentTenant.id : null;
  const currentUser = state.user.currentUser;
  const hospitals = state.hospitals?.list || [] ;
  const surgeons = state.surgeons?.list || [] ;
  const path = collections.LEADS_COLLECTION(tenantId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let leads = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();
          leads = [...leads, {
            ...data,
            id,
            createdAt: data.createdAt ? data.createdAt.toDate() : null,
            updatedAt: data.updatedAt ? data.updatedAt.toDate() : null,
            date: data.date ? data.date.toDate() : null,
            pipelineUpdatedAt: data.pipelineUpdatedAt ? data.pipelineUpdatedAt.toDate() : null,
            stageUpdatedAt: data.stageUpdatedAt ? data.stageUpdatedAt.toDate() : null,
            lostAt: data.lostAt ? data.lostAt.toDate() : null,
            lostDate: data.lostDate ? data.lostDate.toDate() : null,
            wonAt: data.wonAt ? data.wonAt.toDate() : null,
          }];
        });

        if ([SALES_REP, EXTERNAL_SALES_REP, SURGEON, PRACTICE_MANAGER].includes(currentUser.role) && !hasAllSurgeonsAccess(currentUser)) {
          // if (!currentUser.assignedSurgeons || !currentUser.assignedSurgeons.length) {
          //   return dispatch(setLeads([]));
          // }

          leads = leads.filter((l) => canAccessSurgeon(currentUser, l.surgeon) || l.createdBy === currentUser.uid || l.owner === currentUser.uid);
        }

        if (territoryRoles?.includes(currentUser.role) && !!currentUser.territoryVisibility) {
          const userHospitals = hospitals?.filter((h) => h.territories?.some((territory) => currentUser?.assignedTerritories?.includes(territory)));

          leads = leads?.filter((l) => {
            const surgeon = surgeons.find((s) => s.id === l.surgeon);
            return surgeon?.hospitals?.some((hospitalId) => userHospitals.find((h) => h.id === hospitalId)) || l.createdBy === currentUser.uid || l.owner === currentUser.uid
          });
        }

        return dispatch(setLeads(orderBy(leads, 'title', 'asc')));
      }
    });
};

export const subscribeToLeadActivity = (leadId) => (dispatch, getState) => {
  const state = getState();
  const tenant = state?.tenant?.currentTenant;
  const tenantId = tenant?.id;
  const path = collections.LEAD_ACTIVITY_COLLECTION(tenantId, leadId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let activity = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();

          activity = [...activity, {
            ...data,
            id,
            createdAt: data.createdAt ? data.createdAt.toDate() : '',
            bookingData: data?.bookingData ? {
              ...data?.bookingData,
              date: data.bookingData?.date ? data.bookingData.date?.toDate() : '',
            } : null
          }];
        });

        return dispatch(setLeadActivity(orderBy(activity, 'createdAt', 'desc')));
      },
    });
};

export const subscribeToLeadDocuments = (leadId) => (dispatch, getState) => {
  const state = getState();
  const tenant = state?.tenant?.currentTenant;
  const tenantId = tenant?.id;
  const path = collections.LEAD_DOCUMENTS_COLLECTION(tenantId, leadId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let documents = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();

          documents = [...documents, {
            ...data,
            id,
            createdAt: data.createdAt ? data.createdAt.toDate() : '',
          }];
        });

        return dispatch(setLeadDocuments(orderBy(documents, 'createdAt', 'desc')));
      },
    });
};

export const subscribeToLeadNotes = (leadId) => (dispatch, getState) => {
  const state = getState();
  const tenant = state?.tenant?.currentTenant;
  const tenantId = tenant?.id;
  const path = collections.LEAD_NOTES_COLLECTION(tenantId, leadId);

  return firebase
    .db
    .collection(path)
    .onSnapshot({
      error: (e) => console.error(e),
      next: (querySnapshot) => {
        let notes = [];
        querySnapshot.forEach((documentSnapshot) => {
          const id = documentSnapshot.id;
          const data = documentSnapshot.data();

          notes = [...notes, {
            ...data,
            id,
            createdAt: data.createdAt ? data.createdAt.toDate() : '',
          }];
        });

        return dispatch(setLeadNotes(orderBy(notes, 'createdAt', 'desc')));
      },
    });
};
